import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Building2, Loader2, MapPin, AlertTriangle, QrCode } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useBuilding, useBuildings, useRoom, useRooms, useRoomAssets, useCreateReport, uploadToS3, useGPSSampler } from '@/hooks'
import { PageHeader, ChecklistEditor, ImageUpload } from '@/components'
import type { UploadedImage } from '@/components'
import { api } from '@/services'
import type { Image } from '@/types'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ChecklistItem } from '@/types'
import type { CreateUploadURLResponse } from '@/hooks'

const reportFormSchema = z.object({
  building_id: z.string().min(1, 'Please select a building'),
  room_id: z.string().min(1, 'Please select a room'),
  notes: z.string().max(2000, 'Notes are too long').optional(),
})

type ReportFormValues = z.infer<typeof reportFormSchema>

export function ReportCreatePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()

  // Pre-fill from QR code navigation (roomId passed via location state)
  const prefilledRoomId = (location.state as { roomId?: string } | null)?.roomId ?? ''

  const { data: buildings, isLoading: buildingsLoading } = useBuildings(effectiveOrgId)
  const { data: prefilledRoom } = useRoom(prefilledRoomId)
  const { data: prefilledBuilding } = useBuilding(prefilledRoom?.building_id ?? '')
  const createMutation = useCreateReport()

  const { state: gpsState, coords: gpsCoords, retry: retryGPS } = useGPSSampler()

  const isAdminOrSuperAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  // Employees are blocked from submitting while GPS is still acquiring.
  const gpsBlocking = !isAdminOrSuperAdmin && gpsState === 'acquiring'

  // Super admin with no org selected
  if (user?.role === 'super_admin' && !effectiveOrgId && !prefilledRoomId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeader
            title="Create Report"
            description="Submit a new cleaning report for a room"
          />
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Organization Selected</h3>
            <p className="text-muted-foreground text-center max-w-md">
              Select an organization using the switcher in the header to create a report.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const form = useForm<ReportFormValues>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      building_id: '',
      room_id: '',
      notes: '',
    },
  })

  const selectedBuildingId = form.watch('building_id')
  const selectedRoomId = form.watch('room_id')

  const { data: rooms, isLoading: roomsLoading } = useRooms(selectedBuildingId)
  const { data: roomAssets, isLoading: assetsLoading } = useRoomAssets(selectedRoomId)

  // Pre-fill building from QR code navigation.
  const buildingPrefilledRef = useRef(false)
  useEffect(() => {
    if (prefilledRoom && !buildingPrefilledRef.current) {
      buildingPrefilledRef.current = true
      form.setValue('building_id', prefilledRoom.building_id)
    }
  }, [prefilledRoom, form])

  // Pre-fill the room only once its building's room list has loaded, so the
  // matching <SelectItem> exists and Radix Select can display its name.
  const roomPrefilledRef = useRef(false)
  useEffect(() => {
    if (
      prefilledRoom &&
      !roomPrefilledRef.current &&
      rooms?.some((r) => r.id === prefilledRoom.id)
    ) {
      roomPrefilledRef.current = true
      form.setValue('room_id', prefilledRoom.id)
    }
  }, [prefilledRoom, rooms, form])

  // Checklist items state - populated from room assets
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([])

  // Pending images to upload after report creation
  const [pendingImages, setPendingImages] = useState<UploadedImage[]>([])
  const [isUploadingImages, setIsUploadingImages] = useState(false)
  // Store reportId after creation for image uploads
  const createdReportIdRef = useRef<string | null>(null)

  // When room changes, reset checklist with assets from that room
  useEffect(() => {
    if (roomAssets) {
      const items: ChecklistItem[] = roomAssets.map((asset) => ({
        asset_id: asset.id,
        asset_name: asset.name,
        checked: false,
        notes: undefined,
      }))
      setChecklistItems(items)
    } else {
      setChecklistItems([])
    }
  }, [roomAssets])

  // Upload images after report is created
  const uploadPendingImages = useCallback(
    async (reportId: string, images: UploadedImage[]) => {
      const filesToUpload = images.filter(
        (img) => img.status === 'pending' && img.file
      )
      if (filesToUpload.length === 0) return

      setIsUploadingImages(true)

      for (const image of filesToUpload) {
        try {
          // Update status to uploading
          setPendingImages((prev) =>
            prev.map((img) =>
              img.id === image.id ? { ...img, status: 'uploading' as const, progress: 0 } : img
            )
          )

          // Get presigned URL
          const response = await api.post<CreateUploadURLResponse>(
            `/reports/${reportId}/images`,
            {
              content_type: image.file.type,
              filename: image.file.name,
              content_length: image.file.size,
            }
          )

          // Upload to S3
          await uploadToS3(response.upload_url, image.file, (progress) => {
            setPendingImages((prev) =>
              prev.map((img) =>
                img.id === image.id ? { ...img, progress } : img
              )
            )
          })

          // Mark as success
          setPendingImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? { ...img, id: response.image_id, status: 'success' as const, progress: 100 }
                : img
            )
          )
        } catch (error) {
          // Mark as error
          setPendingImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? {
                    ...img,
                    status: 'error' as const,
                    error: error instanceof Error ? error.message : 'Upload failed',
                  }
                : img
            )
          )
        }
      }

      setIsUploadingImages(false)
    },
    []
  )

  // Placeholder function for ImageUpload - not used in create mode (deferUpload=true)
  const getUploadUrl = useCallback(
    async (_contentType: string, _filename: string, _contentLength: number) => {
      throw new Error('Images will be uploaded after report is created')
    },
    []
  )

  async function onSubmit(values: ReportFormValues) {
    try {
      const report = await createMutation.mutateAsync({
        building_id: values.building_id,
        room_id: values.room_id,
        checklist: checklistItems,
        notes: values.notes || undefined,
        gps_latitude: gpsCoords?.latitude,
        gps_longitude: gpsCoords?.longitude,
      })

      createdReportIdRef.current = report.id

      // Upload any pending images
      if (pendingImages.length > 0) {
        await uploadPendingImages(report.id, pendingImages)
      }

      // Build optimistic image records so the detail page can render the Photos
      // card immediately with "Processing…" tiles while the S3 event handler
      // flips server-side status from pending to confirmed. The local object-URL
      // preview lets us show a thumbnail under the spinner.
      const now = new Date().toISOString()
      const optimisticImages: Image[] = pendingImages
        .filter((img) => img.status === 'success')
        .map((img) => ({
          id: img.id,
          report_id: report.id,
          s3_key: '',
          status: 'pending',
          download_url: img.preview,
          created_at: now,
          updated_at: now,
        }))

      navigate(`/reports/${report.id}`, {
        state: optimisticImages.length > 0 ? { optimisticImages } : undefined,
      })
    } catch {
      // Error is handled by React Query mutation state (createMutation.error)
    }
  }

  if (user?.role === 'employee' && !prefilledRoomId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeader
            title="Create Report"
            description="Submit a new cleaning report for a room"
          />
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <QrCode className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Scan a Room QR Code</h3>
            <p className="text-muted-foreground text-center max-w-md">
              To create a cleaning report, scan the QR code posted in the room you want to report on.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          title="Create Report"
          description="Submit a new cleaning report for a room"
        />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* GPS status — only shown to non-admins */}
          {!isAdminOrSuperAdmin && gpsState === 'error' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>
                  This cleaning may be flagged as invalid or falsified without GPS.
                  Please enable location access and try again.
                </span>
                <Button type="button" variant="outline" size="sm" onClick={retryGPS}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {!isAdminOrSuperAdmin && gpsState === 'acquiring' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 animate-pulse" />
              Acquiring your location…
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Location</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {prefilledRoomId ? (
                // QR code flow — room is locked, show as read-only text
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Building</p>
                    <p className="font-medium">
                      {prefilledBuilding?.name ?? <span className="text-muted-foreground">Loading…</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Room</p>
                    <p className="font-medium">
                      {prefilledRoom
                        ? <>
                            {prefilledRoom.name}
                            {prefilledRoom.floor && <span className="text-muted-foreground"> · Floor {prefilledRoom.floor}</span>}
                          </>
                        : <span className="text-muted-foreground">Loading…</span>
                      }
                    </p>
                  </div>
                </div>
              ) : (
                // Manual flow — full dropdowns
                <>
                  <FormField
                    control={form.control}
                    name="building_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Building</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value)
                            form.setValue('room_id', '')
                          }}
                          value={field.value}
                          disabled={buildingsLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a building" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {buildings?.map((building) => (
                              <SelectItem key={building.id} value={building.id}>
                                {building.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="room_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Room</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!selectedBuildingId || roomsLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={
                                  !selectedBuildingId
                                    ? 'Select a building first'
                                    : 'Select a room'
                                }
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {rooms?.map((room) => (
                              <SelectItem key={room.id} value={room.id}>
                                {room.name}
                                {room.floor && ` (Floor ${room.floor})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {selectedRoomId && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Checklist</CardTitle>
              </CardHeader>
              <CardContent>
                {assetsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">
                      Loading checklist...
                    </span>
                  </div>
                ) : (
                  <ChecklistEditor
                    items={checklistItems}
                    onChange={setChecklistItems}
                  />
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUpload
                images={pendingImages}
                onChange={setPendingImages}
                getUploadUrl={getUploadUrl}
                maxImages={10}
                deferUpload
                disabled={createMutation.isPending || isUploadingImages}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        placeholder="Add any additional notes about this cleaning report (optional)"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {createMutation.error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{createMutation.error.message}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/reports')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || isUploadingImages || gpsBlocking}>
              {gpsBlocking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Acquiring Location…
                </>
              ) : (createMutation.isPending || isUploadingImages) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isUploadingImages ? 'Uploading Images...' : 'Submitting...'}
                </>
              ) : (
                'Submit Report'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
